// mds-dojo — dojo/_base/Color: the dojo Color class (r/g/b/a, fromString/fromHex/
// fromRgb/fromArray, toHex/toCss/toString). Faithful to dojo semantics incl.
// clamping and the named base colors.
//
// Authored as a PLAIN CONSTRUCTOR (not a native ES class), exactly like real
// dojo/_base/Color.js: dojo Colors are callable/appliable, and dijit/ColorPalette's
// "dye" subclass extends Color via declare([Color]) — the faithful declare runtime
// chain-constructs a base with `Color.apply(this,...)`, which a native ES `class`
// forbids ("cannot be invoked without 'new'"). A plain ctor supports both `new
// Color()` and use as a declare base. API is identical to the previous class form.

const named: Record<string, number[]> = {
  black: [0, 0, 0], silver: [192, 192, 192], gray: [128, 128, 128], white: [255, 255, 255],
  maroon: [128, 0, 0], red: [255, 0, 0], purple: [128, 0, 128], fuchsia: [255, 0, 255],
  green: [0, 128, 0], lime: [0, 255, 0], olive: [128, 128, 0], yellow: [255, 255, 0],
  navy: [0, 0, 128], blue: [0, 0, 255], teal: [0, 128, 128], aqua: [0, 255, 255],
  transparent: [0, 0, 0, 0],
};

function confine(c: number, low: number, high: number): number {
  return c < low ? low : c > high ? high : c;
}

const Color: any = function Color(this: any, color?: any) {
  if (color) { this.setColor(color); }
};

// prototype defaults (dojo semantics — overwritten per-instance by setColor/_set)
Color.prototype.r = 255;
Color.prototype.g = 255;
Color.prototype.b = 255;
Color.prototype.a = 1;

Color.prototype._set = function (this: any, r: number, g: number, b: number, a: number): void {
  this.r = r; this.g = g; this.b = b; this.a = a;
};

Color.prototype.setColor = function (this: any, color: any): any {
  if (typeof color === 'string') {
    Color.fromString(color, this);
  } else if (Array.isArray(color)) {
    Color.fromArray(color, this);
  } else if (color) {
    this._set(color.r, color.g, color.b, color.a != null ? color.a : 1);
    if (!(color instanceof Color)) { this.sanitize(); }
  }
  return this;
};

Color.prototype.sanitize = function (this: any): any {
  this.r = Math.round(confine(this.r, 0, 255));
  this.g = Math.round(confine(this.g, 0, 255));
  this.b = Math.round(confine(this.b, 0, 255));
  this.a = confine(this.a, 0, 1);
  return this;
};

Color.prototype.toRgb = function (this: any): number[] {
  return [this.r, this.g, this.b];
};

Color.prototype.toRgba = function (this: any): number[] {
  return [this.r, this.g, this.b, this.a];
};

Color.prototype.toHex = function (this: any): string {
  return '#' + (['r', 'g', 'b'] as const).map((x) => {
    const s = (this as any)[x].toString(16);
    return s.length < 2 ? '0' + s : s;
  }).join('');
};

Color.prototype.toCss = function (this: any, includeAlpha?: boolean): string {
  const rgb = this.r + ', ' + this.g + ', ' + this.b;
  return (includeAlpha ? 'rgba(' + rgb + ', ' + this.a : 'rgb(' + rgb) + ')';
};

Color.prototype.toString = function (this: any): string {
  return this.toCss(true);
};

// --- statics ---
Color.named = named;

// Real dojo/_base/Color.js defines Color.blendColors (used by dojox/gfx/gradient +
// dojox/color/_base).
Color.blendColors = function (start: any, end: any, weight: number, obj?: any): any {
  const t = obj || new Color();
  t.r = Math.round(start.r + (end.r - start.r) * weight);
  t.g = Math.round(start.g + (end.g - start.g) * weight);
  t.b = Math.round(start.b + (end.b - start.b) * weight);
  t.a = start.a + (end.a - start.a) * weight;
  return t.sanitize();
};

Color.fromHex = function (color: string, obj?: any): any {
  const t = obj || new Color();
  const bits = (color.length === 4) ? 4 : 8;
  const mask = (1 << bits) - 1;
  let c = Number('0x' + color.substr(1));
  if (isNaN(c)) { return null; }
  (['b', 'g', 'r'] as const).forEach((x) => {
    const cc = c & mask;
    c >>= bits;
    (t as any)[x] = bits === 4 ? 17 * cc : cc;
  });
  t.a = 1;
  return t;
};

Color.fromRgb = function (color: string, obj?: any): any {
  const m = color.toLowerCase().match(/^rgba?\(([\s\.,0-9%]+)\)/);
  if (!m) { return null; }
  const c = m[1].split(/\s*,\s*/);
  const t = obj || new Color();
  t.r = confine(parseFloat(c[0]), 0, 255);
  t.g = confine(parseFloat(c[1]), 0, 255);
  t.b = confine(parseFloat(c[2]), 0, 255);
  t.a = c[3] != null ? confine(parseFloat(c[3]), 0, 1) : 1;
  return t;
};

Color.fromArray = function (a: number[], obj?: any): any {
  const t = obj || new Color();
  t._set(Number(a[0]), Number(a[1]), Number(a[2]), Number(a[3]));
  if (isNaN(t.a)) { t.a = 1; }
  return t.sanitize();
};

Color.fromString = function (str: string, obj?: any): any {
  const a = named[str.toLowerCase()];
  return (a && Color.fromArray(a, obj)) || Color.fromRgb(str, obj) || Color.fromHex(str, obj);
};

export { Color };
export default Color;
